/**
 * 任务记忆管理适配器
 * 专门处理任务相关的记忆，包括任务模式学习、智能建议等
 */

import { MemoryClient } from '../memory-client'
import type {
  MemoryConfig,
  TaskMemory,
  TaskPattern,
  TaskOutcome,
  TaskSuggestion,
  SearchResult
} from '../types'

/**
 * 任务接口（简化版，实际项目中应该从主项目导入）
 */
interface Task {
  id: string
  name: string
  type: string
  priority?: 'low' | 'normal' | 'high' | 'urgent'
  status?: string
  estimatedTime?: string
  actualTime?: string
  tags?: string[]
  description?: string
  owner?: string
  dependencies?: string[]
}

/**
 * 任务记忆适配器
 */
export class TaskMemoryAdapter {
  private memory: MemoryClient
  private userId: string
  private projectId?: string

  constructor(config: MemoryConfig) {
    this.memory = new MemoryClient(config)
    this.userId = config.userId || 'default'
    this.projectId = config.projectId
  }

  /**
   * 学习任务模式
   * 记录任务的完成情况，用于未来的智能建议
   */
  async learnTaskPattern(params: {
    task: Task
    outcome: TaskOutcome
  }): Promise<void> {
    const { task, outcome } = params

    // 构建任务模式描述
    const patternDescription = this.buildTaskPatternDescription(task, outcome)
    
    // 存储任务模式
    await this.memory.add(patternDescription, {
      category: 'task_pattern',
      tags: [
        ...(task.tags || []),
        task.type,
        `priority:${task.priority || 'normal'}`,
        `status:${outcome.status}`
      ],
      importance: this.calculateImportance(outcome)
    })

    // 如果有挑战和学习记录，单独存储
    if (outcome.challenges?.length) {
      for (const challenge of outcome.challenges) {
        await this.memory.add(
          `任务 "${task.name}" 遇到的挑战: ${challenge}`,
          {
            category: 'learning',
            tags: ['challenge', task.type]
          }
        )
      }
    }

    if (outcome.learnings?.length) {
      for (const learning of outcome.learnings) {
        await this.memory.add(
          `从任务 "${task.name}" 中学到: ${learning}`,
          {
            category: 'learning',
            tags: ['best_practice', task.type]
          }
        )
      }
    }
  }

  /**
   * 获取任务建议
   * 基于历史记忆提供智能建议
   */
  async suggestTask(
    description: string,
    context?: {
      type?: string
      tags?: string[]
    }
  ): Promise<TaskSuggestion> {
    // 搜索相似的任务模式
    const similarPatterns = await this.memory.search(description, {
      filter: {
        category: 'task_pattern',
        tags: context?.tags
      },
      limit: 10
    })

    // 分析模式，提取建议
    const suggestion = this.analyzePatterns(similarPatterns, description)
    
    // 搜索相关的挑战和最佳实践
    const challenges = await this.memory.search(description, {
      filter: {
        category: 'learning',
        tags: ['challenge']
      },
      limit: 5
    })

    const bestPractices = await this.memory.search(description, {
      filter: {
        category: 'learning',
        tags: ['best_practice']
      },
      limit: 5
    })

    // 整合建议
    return {
      ...suggestion,
      tips: [
        ...challenges.map(r => `注意: ${r.memory.content}`),
        ...bestPractices.map(r => `建议: ${r.memory.content}`)
      ]
    }
  }

  /**
   * 查找相似的历史任务
   */
  async findSimilarTasks(
    task: Partial<Task>,
    limit: number = 5
  ): Promise<Array<{ task: Task; similarity: number }>> {
    const query = this.buildTaskQuery(task)
    
    const results = await this.memory.search(query, {
      filter: {
        category: 'task_pattern'
      },
      limit
    })

    return results.map(result => ({
      task: this.extractTaskFromMemory(result.memory.content),
      similarity: result.score
    }))
  }

  /**
   * 获取任务完成时间预测
   */
  async predictTaskTime(
    task: Partial<Task>
  ): Promise<{
    estimated: string
    confidence: number
    basedOn: number
  }> {
    const similarTasks = await this.findSimilarTasks(task, 10)
    
    if (similarTasks.length === 0) {
      return {
        estimated: '未知',
        confidence: 0,
        basedOn: 0
      }
    }

    // 计算平均时间
    const times = similarTasks
      .map(({ task }) => task.actualTime || task.estimatedTime)
      .filter(Boolean)
      .map(time => this.parseTimeToHours(time!))

    const avgHours = times.reduce((sum, h) => sum + h, 0) / times.length
    const estimated = this.formatHours(avgHours)
    
    // 计算置信度（基于相似度和样本数量）
    const avgSimilarity = similarTasks.reduce((sum, { similarity }) => 
      sum + similarity, 0
    ) / similarTasks.length
    const confidence = Math.min(avgSimilarity * (times.length / 10), 1)

    return {
      estimated,
      confidence,
      basedOn: times.length
    }
  }

  /**
   * 获取任务依赖建议
   */
  async suggestDependencies(
    task: Partial<Task>
  ): Promise<string[]> {
    const query = `${task.name || ''} ${task.type || ''} 依赖 前置任务`
    
    const results = await this.memory.search(query, {
      filter: {
        category: 'task_pattern'
      },
      limit: 10
    })

    // 提取依赖关系
    const dependencies = new Set<string>()
    
    results.forEach(result => {
      const deps = this.extractDependencies(result.memory.content)
      deps.forEach(dep => dependencies.add(dep))
    })

    return Array.from(dependencies)
  }

  /**
   * 记录任务执行反馈
   */
  async recordFeedback(
    taskId: string,
    feedback: {
      helpful: boolean
      accuracy: number // 0-1
      comments?: string
    }
  ): Promise<void> {
    await this.memory.add(
      `任务 ${taskId} 的反馈: 准确度 ${feedback.accuracy}, ` +
      `有用性 ${feedback.helpful ? '是' : '否'}` +
      (feedback.comments ? `, 评论: ${feedback.comments}` : ''),
      {
        category: 'feedback',
        tags: ['task_feedback'],
        importance: feedback.helpful ? 0.8 : 0.2
      }
    )
  }

  /**
   * 获取任务统计信息
   */
  async getTaskStats(): Promise<{
    totalPatterns: number
    byType: Record<string, number>
    byPriority: Record<string, number>
    averageCompletionRate: number
  }> {
    const stats = await this.memory.getStats({
      category: 'task_pattern'
    })

    // 分析任务类型和优先级分布
    const patterns = await this.memory.search('*', {
      filter: { category: 'task_pattern' },
      limit: 1000
    })

    const byType: Record<string, number> = {}
    const byPriority: Record<string, number> = {}
    let completedCount = 0

    patterns.forEach(({ memory }) => {
      // 从标签中提取类型和优先级
      memory.metadata.tags?.forEach(tag => {
        if (tag.startsWith('priority:')) {
          const priority = tag.replace('priority:', '')
          byPriority[priority] = (byPriority[priority] || 0) + 1
        }
        if (tag.includes('completed')) {
          completedCount++
        }
      })
      
      // 假设第一个非 priority/status 标签是类型
      const typeTag = memory.metadata.tags?.find(
        tag => !tag.includes(':') && !tag.includes('completed')
      )
      if (typeTag) {
        byType[typeTag] = (byType[typeTag] || 0) + 1
      }
    })

    return {
      totalPatterns: stats.total,
      byType,
      byPriority,
      averageCompletionRate: stats.total > 0 
        ? completedCount / stats.total 
        : 0
    }
  }

  // ============= 私有方法 =============

  /**
   * 构建任务模式描述
   */
  private buildTaskPatternDescription(
    task: Task,
    outcome: TaskOutcome
  ): string {
    const parts = [
      `任务: ${task.name}`,
      `类型: ${task.type}`,
      `优先级: ${task.priority || 'normal'}`,
      `预估时间: ${task.estimatedTime || '未知'}`,
      `实际时间: ${outcome.actualTime || task.actualTime || '未知'}`,
      `结果: ${outcome.status}`,
      `质量: ${outcome.quality || '未评估'}`
    ]

    if (task.tags?.length) {
      parts.push(`标签: ${task.tags.join(', ')}`)
    }

    if (task.dependencies?.length) {
      parts.push(`依赖: ${task.dependencies.join(', ')}`)
    }

    if (outcome.wouldDoAgain?.length) {
      parts.push(`经验: ${outcome.wouldDoAgain.join('; ')}`)
    }

    return parts.join('\n')
  }

  /**
   * 计算记忆重要性
   */
  private calculateImportance(outcome: TaskOutcome): number {
    let importance = 0.5

    // 根据结果调整
    if (outcome.status === 'completed') {
      importance += 0.2
    }
    
    // 根据质量调整
    const qualityScores = {
      excellent: 0.3,
      good: 0.2,
      fair: 0.1,
      poor: -0.1
    }
    importance += qualityScores[outcome.quality || 'fair'] || 0

    // 有学习记录的更重要
    if (outcome.learnings?.length) {
      importance += 0.1
    }

    return Math.max(0, Math.min(1, importance))
  }

  /**
   * 分析模式，生成建议
   */
  private analyzePatterns(
    patterns: SearchResult[],
    description: string
  ): Omit<TaskSuggestion, 'tips'> {
    if (patterns.length === 0) {
      return {
        estimatedTime: '3d',
        suggestedPriority: 'normal',
        suggestedTags: [],
        similarTasks: [],
        confidence: 0
      }
    }

    // 提取和统计信息
    const priorities: Record<string, number> = {}
    const tags: Record<string, number> = {}
    const times: number[] = []
    const similarTasks: TaskSuggestion['similarTasks'] = []

    patterns.forEach(({ memory, score }) => {
      const task = this.extractTaskFromMemory(memory.content)
      
      // 统计优先级
      if (task.priority) {
        priorities[task.priority] = (priorities[task.priority] || 0) + score
      }

      // 统计标签
      task.tags?.forEach(tag => {
        if (!tag.includes(':')) {
          tags[tag] = (tags[tag] || 0) + score
        }
      })

      // 收集时间
      const time = task.actualTime || task.estimatedTime
      if (time) {
        times.push(this.parseTimeToHours(time))
      }

      // 收集相似任务
      if (score > 0.7) {
        similarTasks.push({
          id: task.id,
          name: task.name,
          similarity: score
        })
      }
    })

    // 计算建议
    const suggestedPriority = this.getMostLikely(priorities) as any || 'normal'
    const suggestedTags = Object.entries(tags)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([tag]) => tag)

    const estimatedTime = times.length > 0
      ? this.formatHours(times.reduce((a, b) => a + b) / times.length)
      : '3d'

    const confidence = Math.min(
      patterns[0].score * (patterns.length / 10),
      1
    )

    return {
      estimatedTime,
      suggestedPriority,
      suggestedTags,
      similarTasks: similarTasks.slice(0, 3),
      confidence
    }
  }

  /**
   * 从记忆内容提取任务信息
   */
  private extractTaskFromMemory(content: string): Task {
    const lines = content.split('\n')
    const task: any = { tags: [] }

    lines.forEach(line => {
      const [key, value] = line.split(': ')
      switch (key) {
        case '任务':
          task.name = value
          task.id = `task-${Date.now()}`
          break
        case '类型':
          task.type = value
          break
        case '优先级':
          task.priority = value
          break
        case '预估时间':
          task.estimatedTime = value
          break
        case '实际时间':
          task.actualTime = value
          break
        case '标签':
          task.tags = value.split(', ')
          break
        case '依赖':
          task.dependencies = value.split(', ')
          break
      }
    })

    return task
  }

  /**
   * 构建任务查询
   */
  private buildTaskQuery(task: Partial<Task>): string {
    const parts = []
    
    if (task.name) parts.push(task.name)
    if (task.type) parts.push(`类型: ${task.type}`)
    if (task.tags?.length) parts.push(task.tags.join(' '))
    if (task.description) parts.push(task.description)
    
    return parts.join(' ')
  }

  /**
   * 解析时间字符串为小时数
   */
  private parseTimeToHours(time: string): number {
    const match = time.match(/(\d+)([hdwm])/)
    if (!match) return 8 // 默认1天

    const [, num, unit] = match
    const value = parseInt(num)

    switch (unit) {
      case 'h': return value
      case 'd': return value * 8
      case 'w': return value * 40
      case 'm': return value * 160
      default: return 8
    }
  }

  /**
   * 格式化小时数为时间字符串
   */
  private formatHours(hours: number): string {
    if (hours < 8) return `${Math.round(hours)}h`
    if (hours < 40) return `${Math.round(hours / 8)}d`
    if (hours < 160) return `${Math.round(hours / 40)}w`
    return `${Math.round(hours / 160)}m`
  }

  /**
   * 提取依赖关系
   */
  private extractDependencies(content: string): string[] {
    const deps: string[] = []
    const lines = content.split('\n')
    
    lines.forEach(line => {
      if (line.includes('依赖:')) {
        const depList = line.split('依赖:')[1].trim()
        deps.push(...depList.split(', '))
      }
    })
    
    return deps
  }

  /**
   * 获取最可能的选项
   */
  private getMostLikely(
    scores: Record<string, number>
  ): string | undefined {
    const entries = Object.entries(scores)
    if (entries.length === 0) return undefined
    
    return entries.reduce((a, b) => a[1] > b[1] ? a : b)[0]
  }
}